import { Injectable } from '@angular/core';
import {
    FormGroup,
    FormControl,
    FormArray,
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../../../core/models/form-group-type';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { CoordinationParametersFormModel } from '../../models/coordination-parameters-form-model';
import { FrequencyPoolFormModel } from '../../models/frequency-pool/frequency-pool-form-model';
import { FrequencyPoolResponse } from '../../models/frequency-pool/frequency-pool-response';

@Injectable({
    providedIn: 'root',
})
export class FrequencyPoolFormService {
    getFormDefinition(): FormGroup<FormGroupType<FrequencyPoolFormModel>> {
        return new FormGroup<FormGroupType<FrequencyPoolFormModel>>({
            frequencyRequestId: new FormControl<number | null>(null),
            frequencyAllocationCode: new FormControl<string | null>(null),
            isSelected: new FormControl<boolean | null>(
                null,
                this.isSelectedFrequencyAllocationValidator(),
            ),
            actionCode: new FormControl<ActionCode | null>(null),
            locationNumber: new FormControl<number | null>(null),
            locationTypeCode: new FormControl<string | null>(null),
            antennaNumber: new FormControl<number | null>(null),
            antennaHeightToTip: new FormControl<number | null>(null),
            powerErp: new FormControl<number | null>(null),
            frequencyLow: new FormControl<number | null>(null),
            frequencyHigh: new FormControl<number | null>(null),
        });
    }

    /**
     * Patches the form value with the response. This is necessary because the API response types are different from the form types.
     * The form values are strings, but the API response values are numbers because it gets serialized as numbers.
     */
    patchFormValue(
        form: FormArray<FormGroup<FormGroupType<FrequencyPoolFormModel>>>,
        response: FrequencyPoolResponse[],
    ): void {
        form.patchValue(
            response.map((frequency) => {
                return {
                    ...frequency,
                    isSelected: frequency.frequencyAllocationCode !== null,
                };
            }),
        );
    }

    /**
     * Gets the distinct frequency allocation codes from the form array.
     * @param formArray
     * @returns
     */
    getDistinctFrequencyAllocationCodes(
        formArray: FormArray<FormGroup<FormGroupType<FrequencyPoolFormModel>>>,
    ): string[] {
        return formArray.controls
            .map((control) => control.controls.frequencyAllocationCode.value)
            .filter((value): value is string => value !== null)
            .filter((value, index, self) => self.indexOf(value) === index);
    }

    /**
     * Will check if a frequency allocation code has been selected and if so,
     * will check if at least one frequency has been selected.
     * If no frequencies are selected, it will return an error.
     * @returns
     */
    private isSelectedFrequencyAllocationValidator(): ValidatorFn {
        return (
            isSelectedControl: AbstractControl,
        ): ValidationErrors | null => {
            const coordinationParametersFormGroup = isSelectedControl?.parent
                ?.parent?.parent as FormGroup<
                NestedFormGroupType<CoordinationParametersFormModel>
            >;

            if (!coordinationParametersFormGroup) {
                return null;
            }

            const frequencyAllocationCodeControl =
                coordinationParametersFormGroup.controls
                    .frequencyAllocationCode;

            if (
                !frequencyAllocationCodeControl ||
                !frequencyAllocationCodeControl.value
            ) {
                return null;
            }

            const frequencyPoolOptionsFormArray =
                coordinationParametersFormGroup.controls
                    .frequencyPoolOptions as FormArray<
                    FormGroup<FormGroupType<FrequencyPoolFormModel>>
                >;

            if (
                !frequencyPoolOptionsFormArray ||
                !frequencyPoolOptionsFormArray.controls
            ) {
                return null;
            }

            const selectedFrequencies =
                frequencyPoolOptionsFormArray.controls.filter(
                    (control) => control.value.isSelected,
                );

            if (selectedFrequencies.length === 0) {
                return {
                    required:
                        'At least one frequency needs to be selected if a frequency allocation code is present.',
                };
            }

            return null;
        };
    }
}
